

import { Route, Routes, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useConvexAuth } from "convex/react";
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from "@vercel/speed-insights/react"

import AuthPage from "./pages/AuthPage";
import { FullScreenLoader } from "./components/ui/full-screen-loader";
import { NetworkStatusBanner } from "./components/banners/NetworkStatusBanner";
import Error from "./pages/error";
import { Suspense, lazy } from "react";
import { usePageTitle } from "./hooks/use-page-title";

// Lazy load components
const Welcome = lazy(() => import("./pages/onboarding_pages/Welcome"));
const NotesOnboard = lazy(() => import("./pages/onboarding_pages/NotesOnboard"));
const LearnOnboard = lazy(() => import("./pages/onboarding_pages/LearnOnboard"));
const OrganizeOnboard = lazy(() => import("./pages/onboarding_pages/OrganizeOnboard"));
const MasterOnboard = lazy(() => import("./pages/onboarding_pages/MasterOnboard"));
const ThemeOnboard = lazy(() => import("./pages/onboarding_pages/ThemeOnboard"));
const UserOnboard = lazy(() => import("./pages/onboarding_pages/UserOnboard"));
const ImportOnboard = lazy(() => import("./pages/onboarding_pages/ImportOnboard"));
const PricingOnboard = lazy(() => import("./pages/onboarding_pages/PricingOnboard"));
const Notes = lazy(() => import("./pages/Notes"));
const Course = lazy(() => import("./pages/Course"));
const CourseSubfolder = lazy(() => import("./pages/CourseSubfolder"));
const Document = lazy(() => import("./pages/Document"));
const Courses = lazy(() => import("./pages/Courses"));
const StudyBoard = lazy(() => import("./pages/StudyBoard"));
const StudySets = lazy(() => import("./pages/StudySets"));
const NewStudySet = lazy(() => import("./pages/NewStudySet"));
const StudySetPreview = lazy(() => import("./pages/StudySetPreview"));
const EditStudySet = lazy(() => import("./pages/EditStudySet"));
const FlashcardStudy = lazy(() => import("./pages/study_modes_pages/FlashcardStudy"));
const QuizStudy = lazy(() => import("./pages/study_modes_pages/QuizStudy"));
const WrittenStudy = lazy(() => import("./pages/study_modes_pages/WrittenStudy"));
const TestStudy = lazy(() => import("./pages/study_modes_pages/TestStudy"));
const StudySetsFolder = lazy(() => import("./pages/StudySetsFolder"));
const StudySetImport = lazy(() => import("./pages/StudySetImport"));
const ImportedFlashcardStudy = lazy(() => import("./pages/ImportedFlashcardStudy"));
const DocumentImport = lazy(() => import("./pages/DocumentImport"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PublishedDocument = lazy(() => import("./pages/PublishedDocument"));
const PublishedPDF = lazy(() => import("./pages/PublishedPDF"));
const PDFEditor = lazy(() => import("./pages/PDFEditor"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

import { NotificationsModal } from "./notifications/NotificationsModal";

const App = () => {
  const { isAuthenticated, isLoading } = useConvexAuth()

  // Update page title based on current route
  usePageTitle();

  return (
    <>
      <NetworkStatusBanner />
      <Toaster
        position="bottom-center"
        expand
        closeButton
        richColors
        style={{ '--border-radius': '1rem' } as React.CSSProperties}
      />
      <Analytics />
      <SpeedInsights />

      {isLoading && <FullScreenLoader label="Loading..." />}

      {!isLoading && isAuthenticated && <NotificationsModal />}

      {!isLoading && !isAuthenticated && (
        <Routes>
          <Route path="/login" element={<AuthPage isSignUp={false} />} />
          <Route path="/login/factor-one" element={<AuthPage isSignUp={false} />} />
          <Route path="/signup" element={<AuthPage isSignUp={true} />} />
          <Route path="/signup/verify-email-address" element={<AuthPage isSignUp={true} />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
          <Route path="/error" element={<Error />} />

          <Route path="/document/published/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Document..." />}>
              <PublishedDocument />
            </Suspense>
          } />

          <Route path="/pdf/published/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading PDF..." />}>
              <PublishedPDF />
            </Suspense>
          } />

          <Route path="/study-sets/import/:token/study/flashcards" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <ImportedFlashcardStudy />
            </Suspense>
          } />
          <Route path="/study-sets/import/study/flashcards" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <ImportedFlashcardStudy />
            </Suspense>
          } />
          <Route path="/study-sets/import/:token" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <StudySetImport />
            </Suspense>
          } />
          <Route path="/study-sets/import" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <StudySetImport />
            </Suspense>
          } />
          <Route path="/documents/import/:token" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <DocumentImport />
            </Suspense>
          } />
          <Route path="/documents/import" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <DocumentImport />
            </Suspense>
          } />
        </Routes>
      )}


      {!isLoading && isAuthenticated && (
        <Routes>
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/signup" element={<Navigate to="/" replace />} />

          <Route
            path="/"
            element={
              <Suspense fallback={<FullScreenLoader label="Loading Dashboard..." />}>
                <Dashboard />
              </Suspense>
            }
          />

          {/* Document Pages */}
          <Route path="/documents/import/:token" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <DocumentImport />
            </Suspense>
          } />
          <Route path="/documents/import" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <DocumentImport />
            </Suspense>
          } />
          <Route path="/document/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <Document />
            </Suspense>
          } />
          <Route path="/document/published/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Note..." />}>
              <PublishedDocument />
            </Suspense>
          } />
          <Route path="/pdf/published/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading PDF..." />}>
              <PublishedPDF />
            </Suspense>
          } />
          <Route path="/document" element={<Navigate to="/notes" replace />} />
          <Route path="/document/published" element={<Navigate to="/notes" replace />} />

          {/* PDF Editor Pages */}
          <Route path="/pdf/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading PDF Editor..." />}>
              <PDFEditor />
            </Suspense>
          } />
          <Route path="/pdf" element={<Navigate to="/notes" replace />} />

          {/* Course Pages */}
          <Route path="/course/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Course..." />}>
              <Course />
            </Suspense>
          } />
          <Route path="/course/:id/:subfolderId" element={
            <Suspense fallback={<FullScreenLoader label="Loading Folder..." />}>
              <CourseSubfolder />
            </Suspense>
          } />
          <Route path="/courses" element={
            <Suspense fallback={<FullScreenLoader label="Loading Courses..." />}>
              <Courses />
            </Suspense>
          } />
          <Route path="/course" element={<Navigate to="/courses" replace />} />

          <Route path="/notes" element={
            <Suspense fallback={<FullScreenLoader label="Loading Notes..." />}>
              <Notes />
            </Suspense>
          } />

          <Route path="/study-board" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Board..." />}>
              <StudyBoard />
            </Suspense>
          } />
          <Route path="/study-sets" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Sets..." />}>
              <StudySets />
            </Suspense>
          } />
          <Route path="/study-sets/new" element={
            <Suspense fallback={<FullScreenLoader label="Loading New Study Set..." />}>
              <NewStudySet />
            </Suspense>
          } />
          <Route path="/study-sets/import/:token/study/flashcards" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Mode..." />}>
              <ImportedFlashcardStudy />
            </Suspense>
          } />
          <Route path="/study-sets/import/study/flashcards" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Mode..." />}>
              <ImportedFlashcardStudy />
            </Suspense>
          } />
          <Route path="/study-sets/import/:token" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <StudySetImport />
            </Suspense>
          } />
          <Route path="/study-sets/import" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <StudySetImport />
            </Suspense>
          } />
          <Route path="/study-sets/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set..." />}>
              <StudySetPreview />
            </Suspense>
          } />
          <Route path="/study-sets/:id/edit" element={
            <Suspense fallback={<FullScreenLoader label="Loading Edit Study Set..." />}>
              <EditStudySet />
            </Suspense>
          } />
          <Route path="/study-sets/folder/:id" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Set Folder..." />}>
              <StudySetsFolder />
            </Suspense>
          } />
          <Route path="/study-sets/folder" element={<Navigate to="/study-sets" replace />} />



          {/* Study Modes */}
          <Route path="/study-sets/:id/study/flashcards" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Mode..." />}>
              <FlashcardStudy />
            </Suspense>
          } />
          <Route path="/study-sets/:id/study/quiz" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Mode..." />}>
              <QuizStudy />
            </Suspense>
          } />
          <Route path="/study-sets/:id/study/written" element={
            <Suspense fallback={<FullScreenLoader label="Loading Study Mode..." />}>
              <WrittenStudy />
            </Suspense>
          } />
          <Route path="/study-sets/:id/study/test" element={
            <Suspense fallback={<FullScreenLoader label="Loading Practice Test..." />}>
              <TestStudy />
            </Suspense>
          } />
          <Route path="/study-sets/:id/study/test/:testId" element={
            <Suspense fallback={<FullScreenLoader label="Loading Practice Test..." />}>
              <TestStudy />
            </Suspense>
          } />
          <Route path="/study-sets/:id/study" element={<Navigate to="/study-sets" replace />} />

          <Route path="/pricing" element={
            <Suspense fallback={<FullScreenLoader label="Loading Pricing..." />}>
              <Pricing />
            </Suspense>
          } />

          {/* Onboarding Pages */}
          <Route path="/onboarding" element={<Navigate to="/onboarding/welcome" replace />} />
          <Route path="/onboarding/welcome" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <Welcome />
            </Suspense>
          } />
          <Route path="/onboarding/notes" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <NotesOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/learn" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <LearnOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/organize" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <OrganizeOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/master" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <MasterOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/theme" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <ThemeOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/user" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <UserOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/import" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <ImportOnboard />
            </Suspense>
          } />
          <Route path="/onboarding/pricing" element={
            <Suspense fallback={<FullScreenLoader label="Loading..." />}>
              <PricingOnboard />
            </Suspense>
          } />

          <Route path="*" element={<Error />} />
        </Routes>
      )}
    </>
  );
};

export default App;
